import React, { MouseEvent, useCallback } from 'react';
import { BsLink45Deg } from '@react-icons/all-files/bs/BsLink45Deg';
import { useContextSelector } from 'use-context-selector';
import { BucketLogData } from '@bucketplace/bucket-log';
import { useSnackbar } from '@bucketplace/design-system';
import { sendDeepLinkAction } from '@bucketplace/owap-sdk';

import { useOwapInfo } from '@/utils';

import { EditorPlaceholder } from '../../../components';
import { EditorContext, initialLinkContextValue, useEditorInfoContext, useImageMapBrazeContext, useLinkContext } from '../../../contexts';

export interface ImageMapBrazeButtonProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  customEventName?: string;
  url?: string;
  message?: string;
  logData?: BucketLogData;
}

export function ImageMapBrazeButton({
  children,
  ...props
}: ImageMapBrazeButtonProps) {
  return (
    <BrazeButton {...props}>
      { children || (
        <EditorPlaceholder
          icon={<BsLink45Deg />}
          message="아이템을 추가해주세요."
        />
      ) }
    </BrazeButton>
  );
}

const BRAZE_PUSH_SETTING_URLS = [
  'https://qa-web.dailyhou.se/push_setting',
  'https://ohou.se/push_setting',
];

function BrazeButton({
  children,
  className,
  style,
  customEventName,
  url,
  message,
  logData,
}: ImageMapBrazeButtonProps): React.ReactElement {
  const { open: openSnackbar } = useSnackbar();
  const { isLoggedIn, onClick: sendBrazeEvent, onSignin } = useImageMapBrazeContext();
  const { onSendLog } = useEditorInfoContext();
  const { element } = useLinkContext();
  const { isOwap } = useOwapInfo();
  const { element: InitialElement } = initialLinkContextValue;
  const LinkElement = element ?? InitialElement;

  const { displayMode } = useContextSelector(
    EditorContext,
    (context) => context!.state,
  );

  const handleClick = useCallback((e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>): void => {
    if (displayMode === 'edit' || !customEventName) {
      return;
    }

    /**
     * 로그인 유저만 braze 전송합니다.
     * 로그인 유저이면서 url 없으면 snackbar 노출합니다.
     */
    if (!isLoggedIn) {
      onSignin?.();
      return;
    }

    // braze 이벤트 전송
    sendBrazeEvent?.(customEventName ?? '');

    // owap 앱인 경우 브레이즈의 경우 예외처리 필요
    const isBrazePushSetting = url && isOwap && BRAZE_PUSH_SETTING_URLS.some((pushUrl) => url.includes(pushUrl));

    if (isBrazePushSetting) {
      e.preventDefault();
      sendDeepLinkAction('move', {
        isNative: true,
        type: 'page',
        url, // 브레이즈의 경우, 주소를 전달
      });
    }

    if (logData) {
      onSendLog?.(logData);
    }

    if (!url) {
      openSnackbar({ content: message || '신청이 완료되었습니다.' });
    }
  }, [displayMode, customEventName, sendBrazeEvent]);

  if (url) {
    return (
      <LinkElement
        to={url}
        className={className}
        style={style}
        openOption="sameTab"
        onClick={handleClick}
      >
        { children }
      </LinkElement>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
    >
      { children }
    </button>
  );
}
